<template>
  <div class="chat-wrapper">
    <v-overlay :value="loading" z-index="4">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <chat-messages
      :key="messagesKey"
      :direct-channel="true"
      :chats-counter="channelsLength"
      :messages="currentMessages"
      :current-chat="currentChannel"
      :current-user="uuid"
      :current-user-picture="user.thumbnailUrl"
      :active-writers="activeWritersNames"
      :parse-output-message-text="parseOutputMessageText"
      :parse-input-message-text="parseInputMessageText"
      :has-full-history="channelWithFullHistory[currentChannel]"
      :emoji="emoji"
      :chat-users="channelUsers(currentChannel)"
      :quick-messages="quickMessages"
      :get-file-url="getFileUrlHandler"
      :get-message-by-timetoken="getMessageByTimetokenHandler"
      :get-resource-by-url="getResourceByUrl"
      :get-resource-by-id="getResourceById"
      :send-existing-resource="sendExistingResource"
      :send-resource="sendResource"
      :get-mime-types="getMimeTypes"
      @addMessage="addMessageHandler"
      @ackMessage="ackMessageHandler"
      @copyMessage="copyMessageHandler"
      @removeMessage="removeMessageHandler"
      @addMessageAction="addMessageActionHandler"
      @removeMessageAction="removeMessageActionHandler"
      @markMessagesAsRead="markMessagesAsReadHandler"
      @sendWritingSignal="sendWritingSignal"
      @downloadResourceByUrl="downloadResourceByUrlHandler"
      @downloadFile="downloadFileHandler"
      @askAi="askAiHandler"
    />
    <ask-ai-dialog
      :show-dialog="showAiDialog"
      :question="aiQuestion"
      :answer="aiAnswer.samiAnswer"
      :loading="aiLoading"
      @close-dialog="closeAiDialog"
      @askQuestion="sendAiQuestionHandler"
      @confirmAnswer="sendAiAnswerHandler"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import ChatCore from '@/mixins/chat/core'
import AskAiDialog from '@/components/dialogs/AskAiDialog.vue'
import ChatMessages from '@/components/chat/ChatMessages.vue'

export default {
  name: 'ChatWrapper',
  mixins: [ChatCore],
  components: {
    ChatMessages,
    AskAiDialog,
  },
  created() {
    this.getQuickMessages()
  },
  mounted() {
    if (this.directChannelId) {
      this.setCurrentChannel(this.directChannelId)
    }
  },
  data() {
    return {
      messageAfterChatCreation: null,
      messagesKey: 0,
      showAiDialog: false,
      aiQuestion: '',
      aiAnswer: {},
      aiProfileId: false,
      aiLoading: false,
    }
  },
  beforeDestroy() {
    this.setCurrentChannel('')
  },
  watch: {
    directChannelId(val) {
      if (val) {
        this.setCurrentChannel(val)

        if (this.messageAfterChatCreation) {
          const msg = this.messageAfterChatCreation
          this.addMessage(msg)
          this.messageAfterChatCreation = null
        }
      }
    },
    $route(to, from) {
      if (to.name == 'Patient Chat') {
        // get new current patient data when patient id changed
        if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
          this.getPatient(to.params.id)
        }
        this.messagesKey++
      }
    },
  },
  computed: {
    ...mapState('userStates', ['user']),
    ...mapState('patientsStates', ['currentPatient']),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('chatStates', [
      'currentChannel',
      'uuid',
      'loading',
      'channelWithFullHistory',
    ]),
    ...mapGetters('chatStates', [
      'currentMessages',
      'channelUsers',
      'activeWritersNames',
      'channelsLength',
      'directMessageUsers',
    ]),
    directChannelId() {
      const uuid = this.currentPatient && this.currentPatient.uuid
      if (!uuid) {
        return ''
      }

      return (
        (this.directMessageUsers.find(user => user.uuid === uuid) || {}).id ||
        ''
      )
    },
  },
  methods: {
    ...mapActions('patientsStates', ['getPatient']),
    ...mapActions('resourcesStates', [
      'getResourceByUrl',
      'getResourceById',
      'sendExistingResource',
      'sendResource',
    ]),
    ...mapActions('chatStates', [
      'createChat',
      'setContactSelectionOptionIsSingle',
      'sendAiQuestion',
      'sendAiAnswer',
    ]),
    createChatHandler() {
      this.setContactSelectionOptionIsSingle(true)
      this.createChat({ contacts: [this.currentPatient] })
    },
    addMessageHandler(message) {
      if (!this.directChannelId) {
        this.messageAfterChatCreation = message
        this.createChat({ contacts: [this.currentPatient] })
      } else {
        this.addMessage(message)
      }
    },
    askAiHandler(messageText, profileId) {
      this.aiQuestion = messageText
      this.aiProfileId = profileId
      this.showAiDialog = true
    },
    async sendAiQuestionHandler(messageText) {
      this.aiLoading = true
      this.sendAiQuestion({
        profileId: this.aiProfileId,
        question: messageText,
      })
        .then(response => {
          this.aiAnswer = response || {}
          this.aiLoading = false
        })
        .catch(() => {
          this.aiLoading = false
        })
    },
    async sendAiAnswerHandler(answer) {
      this.showAiDialog = false
      const aiChatMessage = {
        messageText: answer,
        needAck: false,
        mediaLibraryFiles: null,
      }
      if (!this.currentChannel) {
        this.messageAfterChatCreation = aiChatMessage
        this.createChat({ contacts: [this.currentPatient] })
      } else {
        this.addMessage(aiChatMessage)
      }
      this.sendAiAnswer({
        finalAnswer: answer,
        answerId: this.aiAnswer.id,
      })
      this.aiAnswer = {}
      this.aiQuestion = ''
    },
    closeAiDialog() {
      this.showAiDialog = false
      this.aiAnswer = {}
      this.aiQuestion = ''
      this.aiProfileId = false
    },
  },
}
</script>

<style lang="scss" scoped>
.chat-wrapper {
  position: relative;
  height: 100%;
}
</style>
